<template>
  <Transition name="toast-fade">
    <div v-if="visible" class="toast-overlay">
      <div class="toast-container" :class="typeClass">
        <i class="toast-icon" :class="iconClass"></i>
        <span class="toast-message">{{ message }}</span>
      </div>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'Toast',
  props: {
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'success',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    duration: {
      type: Number,
      default: 3000
    }
  },
  data() {
    return {
      visible: false
    }
  },
  computed: {
    typeClass() {
      return `toast-${this.type}`
    },
    iconClass() {
      const icons = {
        success: 'bi bi-check-circle-fill',
        error: 'bi bi-x-circle-fill',
        warning: 'bi bi-exclamation-triangle-fill',
        info: 'bi bi-info-circle-fill'
      }
      return icons[this.type]
    }
  },
  mounted() {
    this.show()
  },
  methods: {
    show() {
      this.visible = true
      setTimeout(() => {
        this.hide()
      }, this.duration)
    },
    hide() {
      this.visible = false
      setTimeout(() => {
        this.$emit('close')
      }, 500) // Wait for animation to complete
    }
  }
}
</script>

<style scoped>
.toast-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  pointer-events: none;
}

.toast-container {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 16px 24px;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 320px;
  max-width: 500px;
  pointer-events: auto;
}

.toast-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.toast-message {
  font-size: 15px;
  line-height: 1.5;
}

/* Type-specific styles */
.toast-success .toast-icon {
  color: #28a745;
}

.toast-error .toast-icon {
  color: #dc3545;
}

.toast-warning .toast-icon {
  color: #ffc107;
}

.toast-info .toast-icon {
  color: #17a2b8;
}

/* Animation */
.toast-fade-enter-active {
  transition: opacity 0.3s ease;
}

.toast-fade-leave-active {
  transition: opacity 0.2s ease;
}

.toast-fade-enter-active .toast-container {
  animation: toast-in 0.3s ease;
}

.toast-fade-leave-active .toast-container {
  animation: toast-out 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
}

@keyframes toast-in {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes toast-out {
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0.9);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .toast-container {
    min-width: 280px;
    max-width: 90vw;
    padding: 14px 20px;
  }

  .toast-icon {
    font-size: 20px;
  }

  .toast-message {
    font-size: 14px;
  }
}
</style>
